import { useCallback } from 'react';
import {
	type AccentMode,
	type Appearance,
	APPEARANCE_STORAGE_KEY,
	DEFAULT_APPEARANCE,
	type DensityMode,
	useAppearanceStore,
	type WallpaperMode,
} from '../stores/appearance-store';

export type { AccentMode, Appearance, DensityMode, WallpaperMode };
export { APPEARANCE_STORAGE_KEY, DEFAULT_APPEARANCE };

export interface UseAppearanceReturn extends Appearance {
	setWallpaper: (wallpaper: WallpaperMode) => void;
	setGrain: (grain: boolean) => void;
	setDensity: (density: DensityMode) => void;
	setAccent: (accent: AccentMode) => void;
	setLightCallouts: (lightCallouts: boolean) => void;
	setAppearance: (patch: Partial<Appearance>) => void;
	resetAppearance: () => void;
}

// TODO(tauri-store): When `@tauri-apps/plugin-store` ships into the
// desktop binary, swap the localStorage-backed read/write inside
// `appearance-store.ts` for the Tauri Store path (key = `appearance`,
// store file = `appearance.json`). Pack-settings reads the same
// `khal:appearance` localStorage key today; under Tauri the two must
// continue to agree on a single canonical JSON shape (see
// APPEARANCE.md §114 + Decision #6 in desktop-full-redesign WISH.md).
export function useAppearance(): UseAppearanceReturn {
	const wallpaper = useAppearanceStore((s) => s.wallpaper);
	const grain = useAppearanceStore((s) => s.grain);
	const density = useAppearanceStore((s) => s.density);
	const accent = useAppearanceStore((s) => s.accent);
	const lightCallouts = useAppearanceStore((s) => s.lightCallouts);
	const set = useAppearanceStore((s) => s.set);
	const reset = useAppearanceStore((s) => s.reset);

	const setWallpaper = useCallback((next: WallpaperMode) => set({ wallpaper: next }), [set]);
	const setGrain = useCallback((next: boolean) => set({ grain: next }), [set]);
	const setDensity = useCallback((next: DensityMode) => set({ density: next }), [set]);
	const setAccent = useCallback((next: AccentMode) => set({ accent: next }), [set]);
	const setLightCallouts = useCallback((next: boolean) => set({ lightCallouts: next }), [set]);
	const setAppearance = useCallback((patch: Partial<Appearance>) => set(patch), [set]);

	return {
		wallpaper,
		grain,
		density,
		accent,
		lightCallouts,
		setWallpaper,
		setGrain,
		setDensity,
		setAccent,
		setLightCallouts,
		setAppearance,
		resetAppearance: reset,
	};
}
